import partition from "lodash/partition";

import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { PriorityOption } from "@/features/purchasing-priority/purchasing-priority-utils";

export interface PurchasingPriorities {
  templatePriorities: BomPathPurchasingPriority[];
  salesOrderPriorities: BomPathPurchasingPriority[];
}

export interface BomPathPurchasingPriority extends Omit<Tables<"BomPathPurchasingPriority">, "priority"> {
  /** The path string, split by comma and parsed into integers. Each segment is an item id */
  pathSegments: number[];

  /** The target item being affected by this priority record. This is the last path segment. */
  targetItemId: number;

  /** The database stores the priority as a generic number, but in practice we only use a few options. */
  priority: PriorityOption;
}

export async function getTemplatePurchasingPriorities(): Promise<PurchasingPriorities> {
  return getPurchasingPriorities(null);
}

export async function getPurchasingPrioritiesForJob(jobId: number): Promise<PurchasingPriorities> {
  return getPurchasingPriorities(jobId);
}

async function getPurchasingPriorities(jobId: number | null): Promise<PurchasingPriorities> {
  // Fetch all purchasing priorities for the sales order (if a job id was specified), and the template priorities (jobId=null)
  const allPriorities = (
    await supabase
      .from("BomPathPurchasingPriority")
      .select("*")
      .or(`${jobId ? `jobId.eq.${jobId.toFixed(0)},` : ""}jobId.is.null`)
      .order("path", { ascending: true })
      .throwOnError()
  ).data.map(processPurchasingPriorityRecord);

  // Partition the priorities into sales order-specific and template priorities before returning
  const [templatePriorities, salesOrderPriorities] = partition(allPriorities, (x) => x.jobId === null);
  return { templatePriorities, salesOrderPriorities };
}

export function processPurchasingPriorityRecord(
  record: Tables<"BomPathPurchasingPriority">,
): BomPathPurchasingPriority {
  const pathSegments = record.path.split(",").map((segmentString) => parseInt(segmentString, 10));
  return {
    ...record,
    pathSegments,
    targetItemId: pathSegments[pathSegments.length - 1]!,
    priority: record.priority as PriorityOption,
  };
}
